import { useState } from "react";

import {
  ampliCurveState,
  ampliCurveStateLabel,
  ampliInconsistentReasonLabel,
} from "../financialProjection/cashFlowPlanning";
import { regenerateAmpliDisbursement } from "./disbursementApi";
import { money } from "./format";
import {
  normalizeDisbursementCoverage,
  coverageServicesLabel,
} from "./coverage";

const STATE_HINT = Object.freeze({
  ready: "As regras cadastradas já estão refletidas na Projeção Financeira.",
  stale: "A curva anterior continua no ar enquanto a nova é gerada.",
  inconsistent: null, // vem do motivo
  unavailable: "Esta obra ainda não tem previsto canônico para gerar a curva.",
});

// `coverage.percentage` é uma FRAÇÃO (0..1), não basis points. O nome é
// `fractionLabel` de propósito: `format.js#percentLabel` opera em bps (o editor de
// regra grava/lê bps) e os dois já foram confundidos numa revisão.
const fractionLabel = (value) =>
  value == null
    ? "—"
    : `${(value * 100).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`;

/**
 * Folha da linha de cobertura: `money()` lê `useProjectContext` por dentro, e o bloco
 * inteiro é renderizado atrás de `coverage ? ... : null` — chamado ali direto, a
 * contagem de hooks do cabeçalho mudaria no instante em que a cobertura chegasse.
 */
const CoverageHeadline = ({ coverage }) => (
  <div className="ampli-disbursement-coverage__headline">
    <div>
      <span>Cobertura</span>
      <strong>{fractionLabel(coverage.percentage)}</strong>
    </div>
    <div>
      <span>Previsto com regra</span>
      <strong>{money(coverage.ruledCents)}</strong>
    </div>
    <div>
      <span>Previsto total</span>
      <strong>{money(coverage.scheduledCents)}</strong>
    </div>
  </div>
);

const generatedAtLabel = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : `gerado em ${date.toLocaleDateString(
        "pt-BR",
      )} às ${date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
};

const DisbursementHeader = ({
  project,
  readOnly = false,
  onNewRule,
  onNewGroup,
  onOrganizeWithAi,
  onRegenerated,
  moduleState,
}) => {
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(false);
  const displayProject =
    moduleState === undefined
      ? project
      : {
          ...project,
          ampliDisbursement: moduleState
            ? { ...moduleState, state: moduleState.status }
            : null,
        };
  const curve = ampliCurveState(displayProject);
  const coverage = normalizeDisbursementCoverage(
    displayProject?.ampliDisbursement?.coverage,
  );
  const ruledWidth =
    coverage?.percentage != null
      ? Math.min(100, Math.max(0, coverage.percentage * 100))
      : 0;
  const railLabel = `${fractionLabel(
    coverage?.percentage,
  )} do previsto com regra cadastrada`;
  const generatedAt = generatedAtLabel(curve.generatedAt);
  const hint = curve.reason
    ? ampliInconsistentReasonLabel(curve.reason)
    : STATE_HINT[curve.status];

  const regenerate = async () => {
    if (regenerating) return;
    setRegenerating(true);
    setError(false);
    try {
      const result = await regenerateAmpliDisbursement(project?._id);
      if (!result || (result.code !== 200 && result.code !== 202)) {
        setError(true);
        return;
      }
      await onRegenerated?.(result);
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <section
      className="ampli-disbursement-header"
      aria-label="Situação do desembolso Ampli"
    >
      <hgroup className="dashboard-title">
        <h2>Desembolso</h2>
        <p>
          Cadastre a forma de pagar por família de insumo, insumo ou nível da
          EAP. A plataforma redistribui o previsto no tempo e corrige a curva
          sozinha quando o previsto muda ou o item é contratado.
        </p>
      </hgroup>

      <div className="ampli-disbursement-header__state">
        <span
          className={`ampli-disbursement-badge ampli-disbursement-badge--${curve.status}`}
        >
          {ampliCurveStateLabel(curve.status)}
        </span>
        {generatedAt ? (
          <span className="ampli-disbursement-header__timestamp">
            {generatedAt}
          </span>
        ) : null}
        <p className="ampli-disbursement-header__hint">{hint}</p>
      </div>

      {coverage ? (
        <div className="ampli-disbursement-coverage">
          <CoverageHeadline coverage={coverage} />
          {coverage.percentage !== null ? (
            <div
              className="ampli-disbursement-coverage__rail"
              role="img"
              aria-label={railLabel}
            >
              <span
                className="coverage-segment ruled"
                style={{ width: `${ruledWidth}%` }}
              />
              <span
                className="coverage-segment at-sight"
                style={{ width: `${100 - ruledWidth}%` }}
              />
            </div>
          ) : (
            <p>Cobertura percentual indisponível.</p>
          )}
          <p className="ampli-disbursement-coverage__legend">
            {coverageServicesLabel(coverage)}
          </p>
          {coverage.orphanRules?.length > 0 ? (
            <p className="alert alert-info" role="status">
              {`${coverage.orphanRules.length} regra(s) sem previsto abaixo do alvo — nada foi redistribuído por elas.`}
            </p>
          ) : null}
        </div>
      ) : null}

      {!readOnly ? (
        <div className="ampli-disbursement-header__actions">
          <button
            type="button"
            className="button button-accent small"
            onClick={onNewRule}
          >
            Nova regra
          </button>
          <button
            type="button"
            className="button button-secondary small"
            onClick={onNewGroup}
          >
            Novo grupo
          </button>
          <span className="ampli-disbursement-header__ai-action">
            <button
              type="button"
              className="button button-secondary small"
              data-testid="disbursement-organize-ai"
              onClick={onOrganizeWithAi}
            >
              Organizar com IA
            </button>
          </span>
          {curve.status === "stale" || curve.status === "inconsistent" ? (
            <button
              type="button"
              className="button button-secondary small"
              disabled={regenerating}
              onClick={regenerate}
            >
              {regenerating ? "Gerando…" : "Gerar de novo"}
            </button>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <p className="alert alert-error" role="alert">
          Não foi possível gerar a curva agora. Tente novamente.
        </p>
      ) : null}
    </section>
  );
};

export default DisbursementHeader;
